import { AlertParamType } from '@core/referential/alert-param-type.enum'
import { ConditionType } from '@core/referential/condition-type.enum'
import { DTEventType } from '@core/referential/dt-event-type.enum'
import { t } from 'i18next'

export function getEventTypeFromConditionType(conditionType: ConditionType | string | null | undefined): DTEventType | undefined {
  switch (conditionType) {
    case ConditionType.TEMPERATURE: {
      return DTEventType.TEMPERATURE
    }

    case ConditionType.HUMIDITY: {
      return DTEventType.HUMIDITY
    }

    case ConditionType.CHLORINE: {
      return DTEventType.CHLORINE
    }

    case ConditionType.PH: {
      return DTEventType.PH
    }

    default: {
      return undefined
    }
  }
}

export function getChartSeriesLabel(eventType?: DTEventType): string {
  switch (eventType) {
    case DTEventType.HUMIDITY: {
      return t('humidity')
    }

    case DTEventType.CHLORINE: {
      return t('chlorine')
    }

    case DTEventType.PH: {
      return t('ph')
    }

    case DTEventType.AIR_FLOW: {
      return t('airFlow')
    }

    case DTEventType.ILLUMINANCE: {
      return t('illuminance')
    }

    case DTEventType.POWER: {
      return t('power')
    }

    case DTEventType.FLOW_RATE: {
      return t('flowRate')
    }

    case DTEventType.VOLUME: {
      return t('volume')
    }

    case DTEventType.ENERGY: {
      return t('energy')
    }

    case DTEventType.VOLUME_CONSUMPTION: {
      return t('volumeConsumption')
    }

    case DTEventType.ENERGY_CONSUMPTION: {
      return t('energyConsumption')
    }

    default: {
      return t('temperature')
    }
  }
}

export function getActionChartCardTitle(eventType?: DTEventType): string {
  return getChartTitle(eventType) || t('temperatureEvolution')
}

export function getEventTypeUnit(eventType?: DTEventType): string | undefined {
  switch (eventType) {
    case DTEventType.TEMPERATURE: {
      return '°C'
    }

    case DTEventType.WATER_FLOW: {
      return 'm/s'
    }

    case DTEventType.HUMIDITY: {
      return '%'
    }

    case DTEventType.CO2: {
      return 'ppm'
    }

    case DTEventType.TVOC: {
      return 'ppb'
    }

    case DTEventType.CHLORINE: {
      return 'ppm'
    }

    case DTEventType.PH: {
      return ''
    }

    case DTEventType.AIR_FLOW: {
      return 'm³/h'
    }

    case DTEventType.ILLUMINANCE: {
      return 'lx'
    }

    // Compteur de chaleur d'un circuit régulé (puissance thermique, débit, volume cumulé).
    case DTEventType.POWER: {
      return 'W'
    }

    case DTEventType.FLOW_RATE: {
      return 'L/h'
    }

    case DTEventType.VOLUME: {
      return 'L'
    }

    case DTEventType.ENERGY:
    case DTEventType.ENERGY_CONSUMPTION: {
      return 'kWh'
    }

    case DTEventType.VOLUME_CONSUMPTION: {
      return 'L'
    }

    // Sorties analogiques et positions de vanne (V3V, ventilateurs, positionValve).
    // Le synoptique CTA passe déjà `unit: '%'` en dur à makeHistoryOptions et ne
    // consulte jamais cette fonction — aucun risque de suffixe en double.
    case DTEventType.PERCENTAGE: {
      return '%'
    }

    default: {
      return ''
    }
  }
}

export function getAlertParamTypeUnit(eventType?: AlertParamType): string {
  switch (eventType) {
    case AlertParamType.TEMPERATURE_ALERT: {
      return '°C'
    }

    case AlertParamType.WATER_FLOW_ALERT: {
      return 'm/s'
    }

    case AlertParamType.HUMIDITY_ALERT: {
      return '%'
    }

    case AlertParamType.CHLORINE_ALERT: {
      return 'ppm'
    }

    case AlertParamType.PH_ALERT: {
      return ''
    }

    case AlertParamType.CO2_ALERT: {
      return 'ppm'
    }

    case AlertParamType.TVOC_ALERT: {
      return 'ppb'
    }

    default: {
      return ''
    }
  }
}

export function getDefaultEventType(eventTypes: Array<DTEventType>) {
  if (eventTypes.includes(DTEventType.TEMPERATURE)) return DTEventType.TEMPERATURE

  return eventTypes[0]
}

export function getChartTitle(eventType?: DTEventType): string {
  switch (eventType) {
    case DTEventType.TEMPERATURE: {
      return t('temperatureEvolution')
    }

    case DTEventType.WATER_FLOW: {
      return t('waterFlowEvolution')
    }

    case DTEventType.HUMIDITY: {
      return t('humidityEvolution')
    }

    case DTEventType.CHLORINE: {
      return t('chlorineEvolution')
    }

    case DTEventType.PH: {
      return t('phEvolution')
    }

    default: {
      return ''
    }
  }
}

export const availableEventTypes = [
  DTEventType.TEMPERATURE,
  DTEventType.WATER_FLOW,
  DTEventType.HUMIDITY,
  DTEventType.WATER_MOVEMENT,
  DTEventType.CHLORINE,
  DTEventType.PH,
]
